import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import {
	MaybeSuspenseBoundary,
	type MessageProps,
} from '@post-office/placement-common/utils/message-renderer';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { memo, useCallback, useEffect, useState } from 'react';

import { ConfluenceHomeBannerPlacementSkeleton } from './skeleton';
import { MessageRenderer, type renderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { type ConfluenceHomeBannerInitialData } from '../api/types';
import { type ConfluenceHomeBannerPlacement } from '../contracts/Placement';

export type Message = MessageProps<typeof renderer>;

const excludedMessages = new Set(['test-message-inapp', 'message-template-template']);

/**
 * Confluence Home Banner does not have IntlProvider, all it's translations are managed by Confluence
 *
 * @see confluence/next/packages/i18n/src/messages.ts
 */
const ConfluenceHomeBanner = createPlacementComponent<ConfluenceHomeBannerPlacement>(
	PLACEMENT_ID,
	memo(({ initialData, preloadedInitialData }) => {
		const { data, isLoading } = useInitialData<ConfluenceHomeBannerInitialData>(initialData);
		const [{ message, status }, setMessageState] = useState<{
			message: Message | undefined;
			status: 'loading' | 'done';
		}>(() => {
			if (preloadedInitialData) {
				return preloadedInitialData?.messages?.length > 0
					? {
							message: preloadedInitialData?.messages[0] as Message,
							status: 'done',
						}
					: { message: undefined, status: 'done' };
			}

			return { message: undefined, status: 'loading' };
		});

		const handleOnDismissed = useCallback(() => {
			setMessageState({ message: undefined, status: 'done' });
		}, []);

		const isDone = status === 'done';

		useEffect(() => {
			if (!isLoading && !isDone) {
				const newMessage = ((data?.messages || []) as Message[]).filter(
					(dataMessage) => !excludedMessages.has(dataMessage.messageTemplateId),
				)[0];

				setMessageState({
					message: newMessage,
					status: 'done',
				});
			}
		}, [isLoading, isDone, data]);

		if (status === 'loading') {
			return <ConfluenceHomeBannerPlacementSkeleton />;
		}

		return (
			<>
				{message && (
					// Adding @ts-ingore to suppress the error in confluence typecheck and below error is not thrown in post-office typecheck
					// This error exists because confluence is using react 16 and post-office is using react 18
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore - 'ReactNode | ReactElement<any, any>' is not a valid JSX element.
					<MessageRenderer
						{...message}
						id={message.messageInstanceId}
						messageInstanceId={message.messageInstanceId}
						onDismissed={handleOnDismissed}
						SuspenseBoundary={MaybeSuspenseBoundary}
					/>
				)}
			</>
		);
	}),
	{
		choreographer: {
			// Disabling this placement from being choreographed as it is not a pop-up
			isDefaultEnabled: false,
		},
	},
);

export default ConfluenceHomeBanner;
